import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import * as mapboxgl from 'mapbox-gl/esm';
import {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  FeatureSelector,
  IControl,
  LayerSpecification,
  SourceSpecification
} from 'mapbox-gl/esm';
import { CoreoDataLineStyle, CoreoDataPointStyle, CoreoDataPolygonStyle, CoreoDataStyle, CoreoForm } from "../../types";
import AppDatabase from "../db/app-db.service";
import { projectFileExists, projectFilePath, readProjectJsonFile } from "../db/filesystem.service";
import { cdnUrl } from "../image.service";
import { rasterTileExtension } from "./cog-directory";

/** Resolve when the map is idle; immediate if map.idle() is already true. */
export const waitForMapIdle = (map: mapboxgl.Map | null | undefined): Promise<void> => {
  if (!map) {
    return Promise.resolve();
  }
  if (map.idle()) {
    return Promise.resolve();
  }
  return map.once('idle').then(() => { });
};

/** Run fn when idle; returns cancel function. No-op if map is null. */
export const runWhenMapIdle = (
  map: mapboxgl.Map | null | undefined,
  fn: (map: mapboxgl.Map) => void
): (() => void) => {
  if (!map) {
    return () => { };
  }
  if (map.idle()) {
    fn(map);
    return () => { };
  }
  const handler = () => fn(map);
  map.once('idle', handler);
  return () => {
    map.off('idle', handler);
  };
};

export const safelyRemoveFeatureState = (map: mapboxgl.Map, feature: FeatureSelector, state: string) => {
  const existing = map.getFeatureState(feature);
  if (existing && Object.prototype.hasOwnProperty.call(existing, state)) {
    map.removeFeatureState(feature, state);
  }
}

export const safelyAddSource = (map: mapboxgl.Map, id: string, source: SourceSpecification, replace = false) => {
  // If the source already exists and we are not replacing it, return
  const existing = map.getSource(id);
  if (typeof existing !== 'undefined' && !replace) {
    return;
  }
  if (existing && replace) {
    map.removeSource(id);
  }
  map.addSource(id, source);
}

export const safelyAddLayer = (map: mapboxgl.Map, layer: LayerSpecification, replace = false) => {
  const existing = map.getLayer(layer.id);
  if (typeof existing !== 'undefined' && !replace) {
    return
  }
  if (existing && replace) {
    map.removeLayer(layer.id);
  }
  map.addLayer(layer);
}

export const safelyRemoveLayer = (map: mapboxgl.Map, id: string) => {
  if (map && typeof map.getLayer(id) !== 'undefined') {
    map.removeLayer(id);
  }
};

export const safelyRemoveSource = (map: mapboxgl.Map, id: string) => {
  if (map && typeof map.getSource(id) !== 'undefined') {
    map.removeSource(id);
  }
};

export const safelyMoveLayer = (map: mapboxgl.Map, id: string, beforeId?: string) => {
  if (map && typeof map.getLayer(id) !== 'undefined') {
    map.moveLayer(id, beforeId);
  }
}

export const safelyRemoveControl = (map: mapboxgl.Map, control: IControl) => {
  if (map?.hasControl(control)) {
    map.removeControl(control);
  }
}

export const safelySetZoomRange = (map: mapboxgl.Map, layerId: string, minZoom: number, maxZoom: number) => {
  if (map && typeof map.getLayer(layerId) !== 'undefined') {
    map.setLayerZoomRange(layerId, minZoom, maxZoom);
  }
}


/**
 * Zoom the map to a layer's bounds without landing below the zoom at which the
 * layer is drawn. Raster-tiles layers (GeoTIFFs) only exist for a band of zoom
 * levels, so a plain fitBounds on a small layer can stop at, say, z7 when the
 * layer starts at z9 and the user sees nothing. In that case the map is centred
 * on the layer at its minimum zoom instead.
 */
export const fitMapToLayerBounds = (
  map: Pick<mapboxgl.Map, 'fitBounds' | 'cameraForBounds' | 'easeTo'>,
  bounds: mapboxgl.LngLatBoundsLike,
  layer: { layout?: { minZoom?: unknown; [key: string]: unknown } | unknown[] | null } | null | undefined,
  options: { padding: number; maxDuration: number }
): void => {
  const minZoom = layer?.layout && !Array.isArray(layer.layout) ? layer.layout.minZoom : undefined;
  if (typeof minZoom === 'number') {
    const camera = map.cameraForBounds(bounds, { padding: options.padding });
    if (camera && typeof camera.zoom === 'number' && camera.zoom < minZoom) {
      map.easeTo({ center: camera.center, zoom: minZoom, duration: options.maxDuration });
      return;
    }
  }
  map.fitBounds(bounds, options);
};

export const getMapImageryUrl = async (projectId: number, source: string) => {

  if (!Capacitor.isNativePlatform()) {
    return cdnUrl(source);
  }
  return getMapSourceUrl(projectId, source);
}

/**
 * True when native raster tiles can be served from disk (manifest written),
 * or when running on web (CDN is the source of truth).
 */
export const isRasterTileLayerReady = async (projectId: number, layerId: number): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return true;
  }
  return projectFileExists(projectId, 'maps', String(layerId), 'manifest.json');
};

/**
 * Get the tile URL template for a raster-tiles layer.
 * On web: CDN URL template.
 * On native: local files once the manifest exists, otherwise CDN while the pyramid downloads.
 *
 * Online, the extension follows the layer's `layout.format`: COG-backed layers are
 * `webp`, so the request gets the raw block and skips the tile service's PNG
 * transcode; legacy pyramids have no format and keep requesting `.png`. Offline,
 * the extension follows the format the local pack was written in.
 */
export const getRasterTileUrl = async (projectId: number, layerId: number, source: string, format?: string): Promise<string> => {
  const cdnTemplate = `${cdnUrl(source)}/{z}/{x}/{y}.${rasterTileExtension(format)}`;

  if (!Capacitor.isNativePlatform()) {
    return cdnTemplate;
  }

  const localManifest = await readProjectJsonFile<{ format?: string }>(projectId, 'maps', String(layerId), 'manifest.json');
  if (!localManifest) {
    return cdnTemplate;
  }

  const path = projectFilePath(projectId, 'maps', String(layerId));
  const result = await Filesystem.getUri({
    directory: Directory.Data,
    path
  });
  const baseUrl = Capacitor.convertFileSrc(result.uri);
  return `${baseUrl}/{z}/{x}/{y}.${rasterTileExtension(localManifest.format)}`;
}

export const getMapGeoJSONUrl = async (projectId: number, source: string) => {
  if (!Capacitor.isNativePlatform()) {
    return source;
  }
  return getMapSourceUrl(projectId, source);
}

const getMapSourceUrl = async (projectId: number, source: string) => {
  const url = new URL(source);
  const filename = url.pathname.split('/').pop();
  const path = projectFilePath(projectId, 'maps', filename);
  const result = await Filesystem.getUri({
    directory: Directory.Data,
    path
  });
  return Capacitor.convertFileSrc(result.uri);
}

/** Prefer per-feature `__color` on GeoJSON properties (data-driven styling), then existing paint. */
export const withPerFeatureColor = (
  fallback: DataDrivenPropertyValueSpecification<string>
): ExpressionSpecification => {
  return ['coalesce', ['get', '__color'], fallback] as ExpressionSpecification;
};

export const polygonFillPaint = async (style: CoreoDataPolygonStyle): Promise<mapboxgl.FillLayerSpecification['paint']> => {
  const polygonFillPaint: mapboxgl.FillLayerSpecification['paint'] = {
    'fill-color': style.color,
    'fill-opacity': featureStateProperty(
      style.polygonOpacity,
      Math.min(style.polygonOpacity + 0.1, 1),
      Math.max(style.polygonOpacity - 0.1, 0.0),
      Math.min(style.polygonOpacity + 0.2, 1)
    )
  };

  if (style.styleAttributeId) {
    polygonFillPaint['fill-color'] = await getAttributeColorMatch(style.styleAttributeId, style.color);
  }

  polygonFillPaint['fill-color'] = withPerFeatureColor(polygonFillPaint['fill-color']);
  return polygonFillPaint;
}

export const polygonBorderPaint = async (style: CoreoDataPolygonStyle): Promise<mapboxgl.LineLayerSpecification['paint']> => {
  return {
    'line-opacity': style.polygonBorderOpacity,
    'line-width': featureStateProperty(
      style.polygonBorderWidth,
      style.polygonBorderWidth + 2,
      style.polygonBorderWidth,
      style.polygonBorderWidth + 4
    ),
    'line-color': style.polygonBorderColor
  };
};

export const linePaint = async (style: CoreoDataLineStyle): Promise<mapboxgl.LineLayerSpecification['paint']> => {
  const linePaintStyle: mapboxgl.LineLayerSpecification['paint'] = {
    'line-color': ['coalesce', ['get', 'lineColor'], ['literal', style.color]],
    'line-width': featureStateProperty(
      style.lineWidth,
      style.lineWidth + 2,
      style.lineWidth,
      style.lineWidth + 4
    )
  };

  if (style.styleAttributeId) {
    linePaintStyle['line-color'] = await getAttributeColorMatch(style.styleAttributeId, style.color);
  }

  linePaintStyle['line-color'] = withPerFeatureColor(linePaintStyle['line-color']);
  return linePaintStyle;
};

export const lineDashPaint = async (style: CoreoDataLineStyle): Promise<mapboxgl.LineLayerSpecification['paint']> => {
  return {
    'line-color': ['get', 'lineDashColor'],
    'line-dasharray': [2, 4],
    'line-width': featureStateProperty(
      style.lineWidth,
      style.lineWidth + 2,
      style.lineWidth,
      style.lineWidth + 4
    )
  };
}

export const pointPaint = async (style: CoreoDataPointStyle): Promise<mapboxgl.CircleLayerSpecification['paint']> => {
  const pointPaintStyle: mapboxgl.CircleLayerSpecification['paint'] = {
    'circle-color': style.color,
    'circle-opacity': featureStateProperty(
      style.pointOpacity,
      Math.min(style.pointOpacity + 0.1, 1.0),
      Math.max(style.pointOpacity - 0.1, 0.0),
      Math.min(style.pointOpacity + 0.2, 1.0)
    ),
    'circle-radius': featureStateProperty(style.pointRadius, style.pointRadius + 5, style.pointRadius, style.pointRadius + 10),
    'circle-stroke-color': style.pointBorderColor,
    'circle-stroke-width': featureStateProperty(style.pointBorderWidth, style.pointBorderWidth + 2, style.pointBorderWidth, style.pointBorderWidth + 3),
    'circle-stroke-opacity': style.pointBorderOpacity
  };

  if (style.styleAttributeId) {
    pointPaintStyle['circle-color'] = await getAttributeColorMatch(style.styleAttributeId, style.color);
  }
  pointPaintStyle['circle-color'] = withPerFeatureColor(pointPaintStyle['circle-color']);
  return pointPaintStyle;
};

export const featureStateProperty = (baseValue: any, selectedValue: any, deselectedValue: any, hoverValue: any): ExpressionSpecification => {
  return [
    'case',
    ['boolean', ['feature-state', 'selected'], false],
    selectedValue,
    ['boolean', ['feature-state', 'deselected'], false],
    deselectedValue,
    ['boolean', ['feature-state', 'hover'], false],
    hoverValue,
    baseValue
  ]
};

export const getAttributeColorMatch = async (attributeId: number, color: string): Promise<ExpressionSpecification> => {
  const attribute = await AppDatabase.instance.attributes.findById(attributeId);
  const items = await AppDatabase.instance.items.search(q => q.where('collectionId = ?', attribute.collectionId)).toArray();
  const colors = items.reduce((acc, item) => ([...acc, item.key, item.color ?? color]), []);
  return [
    'match',
    ['get', attribute.path],
    ...colors,
    color
  ];
}

export const createDefaultDataLayerStyle = (color: string = '#000000'): CoreoDataStyle => ({
  color,
  contrastColor: null,
  pointRadius: 6,
  pointOpacity: 1,
  pointBorderColor: '#ffffff',
  pointBorderWidth: 1,
  pointBorderOpacity: 1,
  polygonOpacity: 0.3,
  polygonBorderWidth: 1,
  polygonBorderColor: '#ffffff',
  polygonBorderOpacity: 1,
  lineWidth: 1
});

export const formToDataStyle = (form: CoreoForm): CoreoDataStyle => ({
  ...form.style,
  labelAttributeId: form.labelAttributeId,
  labelCollectionAttributeId: form.labelCollectionAttributeId,
  labelCollectionSourceType: form.labelCollectionSourceType,
  styleAttributeId: form.styleAttributeId
});